import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export interface TutorialStep {
  id: string;
  page: 'warband' | 'warchest' | 'templates' | 'exploration' | 'graveyard';
  title: string;
  content: string;
  highlightSelector?: string; // CSS selector for element to highlight
  highlightType?: 'pulse' | 'glow' | 'spotlight' | 'border';
  position?: 'top' | 'bottom' | 'left' | 'right' | 'center';
  actionRequired?: 'click' | 'navigate' | 'input' | 'none';
  nextStepId?: string;
  prevStepId?: string;
  canSkip?: boolean;
}

export interface TutorialData {
  [page: string]: {
    title: string;
    description: string;
    steps: TutorialStep[];
  };
}

interface TutorialContextType {
  isActive: boolean;
  currentStep: TutorialStep | null;
  currentPage: string;
  completedTutorials: string[];
  startTutorial: (page: string) => void;
  nextStep: () => void;
  prevStep: () => void;
  skipTutorial: () => void;
  endTutorial: () => void;
  goToStep: (stepId: string) => void;
}

const TutorialContext = createContext<TutorialContextType | undefined>(undefined);

export const useTutorial = () => {
  const context = useContext(TutorialContext);
  if (context === undefined) {
    throw new Error('useTutorial must be used within a TutorialProvider');
  }
  return context;
};

interface TutorialProviderProps {
  children: ReactNode;
}

export const TutorialProvider: React.FC<TutorialProviderProps> = ({ children }) => {
  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState<TutorialStep | null>(null);
  const [currentPage, setCurrentPage] = useState('');
  const [completedTutorials, setCompletedTutorials] = useState<string[]>([]);

  // Load completed tutorials from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('tutorial_completed');
    if (saved) {
      try {
        setCompletedTutorials(JSON.parse(saved));
      } catch (error) {
        console.warn('Failed to parse tutorial completion data:', error);
      }
    }
  }, []);

  // Save completed tutorials to localStorage
  useEffect(() => {
    localStorage.setItem('tutorial_completed', JSON.stringify(completedTutorials));
  }, [completedTutorials]);

  const startTutorial = (page: string) => {
    // Import tutorial data dynamically
    import('../data/tutorialData').then(({ TUTORIAL_DATA }) => {
      const tutorial = TUTORIAL_DATA[page];
      if (tutorial && tutorial.steps.length > 0) {
        setCurrentPage(page);
        setCurrentStep(tutorial.steps[0]);
        setIsActive(true);
      }
    }).catch(error => {
      console.error('Failed to load tutorial data:', error);
    });
  };

  const nextStep = () => {
    if (!currentStep?.nextStepId) {
      endTutorial();
      return;
    }

    // Load tutorial data to find next step
    import('../data/tutorialData').then(({ TUTORIAL_DATA }) => {
      const tutorial = TUTORIAL_DATA[currentPage];
      const nextStepData = tutorial.steps.find(step => step.id === currentStep.nextStepId);
      if (nextStepData) {
        setCurrentStep(nextStepData);
      } else {
        endTutorial();
      }
    });
  };

  const prevStep = () => {
    if (!currentStep?.prevStepId) {
      return;
    }

    // Load tutorial data to find previous step
    import('../data/tutorialData').then(({ TUTORIAL_DATA }) => {
      const tutorial = TUTORIAL_DATA[currentPage];
      const prevStepData = tutorial.steps.find(step => step.id === currentStep.prevStepId);
      if (prevStepData) {
        setCurrentStep(prevStepData);
      }
    });
  };

  const skipTutorial = () => {
    endTutorial();
  };

  const endTutorial = () => {
    setIsActive(false);
    setCurrentStep(null);
    setCurrentPage('');

    // Mark tutorial as completed
    if (currentPage && !completedTutorials.includes(currentPage)) {
      setCompletedTutorials(prev => [...prev, currentPage]);
    }
  };

  const goToStep = (stepId: string) => {
    // Load tutorial data to find step
    import('../data/tutorialData').then(({ TUTORIAL_DATA }) => {
      const tutorial = TUTORIAL_DATA[currentPage];
      const stepData = tutorial.steps.find(step => step.id === stepId);
      if (stepData) {
        setCurrentStep(stepData);
      }
    });
  };

  const value: TutorialContextType = {
    isActive,
    currentStep,
    currentPage,
    completedTutorials,
    startTutorial,
    nextStep,
    prevStep,
    skipTutorial,
    endTutorial,
    goToStep,
  };

  return (
    <TutorialContext.Provider value={value}>
      {children}
    </TutorialContext.Provider>
  );
};
